import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, ErrorHandler, OnDestroy, OnInit, ViewChild, signal } from '@angular/core'
import { Validators } from '@angular/forms'
import { form, required } from '@angular/forms/signals'
import { MatAutocompleteTrigger } from '@angular/material/autocomplete'
import { MatDialog } from '@angular/material/dialog'
import { MatSnackBar } from '@angular/material/snack-bar'
import { ActivatedRoute, Router } from '@angular/router'
import { UploadTask } from 'firebase/storage'
import { BehaviorSubject, combineLatest, firstValueFrom, Observable, of, Subject } from 'rxjs'
import { filter, map, switchMap, take, takeUntil } from 'rxjs/operators'

import { AsiakasService } from 'app/_angular/service/asiakas/asiakas.service'
import { FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'
import { TilikarttaService } from 'app/_angular/service/tilikartta.service'
import { AreYouSureDialog, AreYouSureDialogData } from 'app/_jaettu-angular/_components/are-you-sure.dialog'
import { asetaArvo, nollaa } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { validoiReaktiivisesti } from 'app/_jaettu-angular/_lomakkeet/reaktiivinen-validaattori'
import { FileSystemFileEntry } from 'app/_jaettu-angular/file-drop/file-drop-dom.types'
import { FileDropEntry } from 'app/_jaettu-angular/file-drop/file-drop-entry'
import { LadataanService } from 'app/_jaettu-angular/service/ladataan.service'
import { TiedostojenLataamisService } from 'app/_jaettu-angular/service/tiedostojen-lataamis.service'

import { DateService } from '@lemon/core/service/date.service'
import { DebugService } from 'app/_angular/service/debug.service'
import { KirjautunutKayttajaService } from 'app/_angular/service/kirjautunut-kayttaja.service'
import {
  AlkusaldoCsvParsePyynto,
  AlkusaldoCsvParseVastaus,
  AlkusaldoHistoriaTyyppi,
  AlkusaldoImportDocument,
  AlkusaldoImportHistoriaDocument,
  AlkusaldoKirjaustenLuontiPyynto,
  AlkusaldoKirjaustenLuontiVastaus,
  AlkusaldoLahdejarjestelma,
  AlkusaldoLahdetili,
  AlkusaldoPoistaPyynto,
  AlkusaldoPoistaVastaus,
  AlkusaldoTiliMappaus,
  AlkusaldoTuontitiedosto,
  AlkusaldoTuontivienti,
  AlkusaldoVirhekoodi,
} from 'app/_jaettu-lemonator/model/alkusaldo-import'
import { Asiakas, Tilikausi } from 'app/_jaettu-lemonator/model/asiakas'
import { Kirjanpitotili } from 'app/_jaettu-lemonator/model/kirjanpito'
import { ArkistoFile, ArkistoFileTiedostoTyyppi, ArkistoFileType, ArkistoService } from 'app/_jaettu-lemonator/service/arkisto.service'
import { AsiakasJaettuService } from 'app/_jaettu-lemonator/service/asiakas-jaettu.service'
import { KirjanpitoUriService } from 'app/_jaettu-lemonator/service/kirjanpito-uri.service'
import { EnvironmentType } from 'app/app.environment'
import { environment } from 'environments/environment'

interface TiliMappausRivi {
  lahdetili: AlkusaldoLahdetili
  kohdetilinumero: string
}

type ValitunTiedostonTila = 'hallintokansiossa' | 'odottaa' | 'ladataan' | 'valmis' | 'virhe'

/** 'auto' = backend tunnistaa lähdejärjestelmän tiedostoista. Sentinel nullin sijaan, koska mat-option ei näytä null-arvoa valittuna. */
type LahdejarjestelmaValinta = AlkusaldoLahdejarjestelma | 'auto'

interface ValittuTuontitiedosto extends AlkusaldoTuontitiedosto {
  koko: number | null
  tila: ValitunTiedostonTila
  latausprosentti: number
  virheviesti: string | null
  /** Tilikaudet, joihin tiedosto liitetään hallintokansiossa (ArkistoFile.tilikaudet) */
  tilikausiAvaimet: string[]
  file?: File
}

const MAKSIMI_NAYTETTAVAT_OSUMAT = 200

@Component({
  selector: 'app-alkusaldo-import',
  templateUrl: './alkusaldo-import.component.html',
  styleUrls: ['./alkusaldo-import.component.css'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AlkusaldoImportComponent implements OnInit, OnDestroy {
  private _ngUnsubscribe = new Subject<void>()
  private _kirjanpitoUriService = new KirjanpitoUriService()

  vaihe: 'valinta' | 'processing' | 'mapping' | 'done' | 'error' = 'processing'
  virhe: string | null = null
  onnistumisviesti: string | null = null
  eiTilikausia = false

  asiakas: Asiakas

  // Reaktiiviset datat
  private _parseVastausSubject = new BehaviorSubject<AlkusaldoCsvParseVastaus | null>(null)

  /** Edellinen import-dokumentti — johdettu asiakasObservablesta switchMapilla */
  edellinenImportObservable: Observable<AlkusaldoImportDocument | null>

  lahdetilitObservable: Observable<AlkusaldoLahdetili[]>
  parsitutViennitObservable: Observable<AlkusaldoTuontivienti[]>
  vientienAikavaliObservable: Observable<{ alku: string; loppu: string } | null>
  luetutTiedostonimetObservable: Observable<string[]>
  tilikarttaTilitObservable: Observable<Kirjanpitotili[]>
  tilikaudetObservable: Observable<Tilikausi[]>

  // Debug
  kirjanpitajaOnDevaajaObservable: Observable<boolean>
  tuontiEncodedUriObservable: Observable<string>
  tuontiUriObservable: Observable<string>

  mappausRivitSubject = new BehaviorSubject<TiliMappausRivi[]>([])

  // Tiedostojen valinta
  lahdejarjestelma = form(signal<LahdejarjestelmaValinta>('auto'))
  tilikausiAvaimet = form(signal<string[]>([]), polku => {
    required(polku)
    // Signal Formsin required ei pidä tyhjää taulukkoa puuttuvana
    validoiReaktiivisesti(polku, Validators.required)
  })
  valitutTiedostotSubject = new BehaviorSubject<ValittuTuontitiedosto[]>([])
  kokonaisprosenttiObservable: Observable<number> = of(0)
  lataaminenKesken = false
  vedetaanPaalle = false
  hylatytTiedostot: string[] = []
  paallekkaisetTiedostot: string[] = []
  private _edellisetMappaukset: AlkusaldoTiliMappaus[] = []
  private _kaynnissaOlevatLataukset: UploadTask[] = []
  private _tuhottu = false

  // Historia
  historiaObservable: Observable<AlkusaldoImportHistoriaDocument[]>
  historiasuodatinSubject = new BehaviorSubject<AlkusaldoHistoriaTyyppi | 'kaikki'>('kaikki')
  suodatettuHistoriaObservable: Observable<AlkusaldoImportHistoriaDocument[]>

  // Aktiivinen tabi (0 = Tuonti, 1 = Historia)
  aktiivinenTabiIndex = 0

  // Päivämäärärajaus (Date-objektit mat-datepickeriä varten)
  alkuPvm = form(signal<Date | null>(null))
  loppuPvm = form(signal<Date | null>(null))

  kaikkiMapattu = false
  piilotaMapatut = false
  luoAlkusaldokirjaukset = form(signal(true))

  /** Lähdetilin numero, jonka rivi on aktiivisessa (muokattavassa) tilassa. Muut rivit näytetään read-only span:einä. */
  aktiivinenRivinNumero: string | null = null

  /** Aktiivisen rivin hakukentän teksti. Aina tyhjä kun rivi aktivoidaan — käyttäjälle näytetään vain etsimistä varten, ei rivin nykyistä valintaa. */
  hakuteksti = ''

  /** Aktiivisen rivin autocomplete-listan suodatetut osumat. Vain yksi rivi voi olla aktiivinen kerrallaan, joten yksi yhteinen kenttä riittää. */
  suodatetutTilit: Kirjanpitotili[] = []

  @ViewChild('aktiivinenTiliInput', { read: ElementRef }) aktiivinenTiliInput: ElementRef<HTMLInputElement> | undefined
  @ViewChild('aktiivinenTiliInput', { read: MatAutocompleteTrigger }) aktiivinenAutocompleteTrigger: MatAutocompleteTrigger | undefined

  /** Kaikki valittavissa olevat kohdetilit. Säilytetään myös listana suodatusta varten ja Mapina nopeaa nimen hakua varten. */
  private _kaikkiKohdetilit: Kirjanpitotili[] = []
  private _kohdetiliNimellaNumerolla = new Map<string, Kirjanpitotili>()

  paivitaKaikkiMapattu(): void {
    const rivit = this.mappausRivitSubject.value
    this.kaikkiMapattu = rivit.length > 0 && rivit.every(r => !!r.kohdetilinumero)
    this.mappausRivitSubject.next(this.mappausRivitSubject.value)
  }

  /**
   * Suodattaa kohdetilit hakutekstillä. Tulokset järjestetään: täysi tilinumero-osuma ensin,
   * sitten numeron alulla matsaavat, ja viimeiseksi nimen perusteella matsaavat. Tämä varmistaa
   * että esim. "2000" löytää tilin 2000 ensimmäisenä, vaikka muita 2000-alkuisia olisi paljon.
   * Lista rajataan, jotta autocomplete-paneli ei jäädytä selainta laajalla tilikartalla.
   */
  private _suodataTilit(haku: string): Kirjanpitotili[] {
    if (!haku) {
      return this._kaikkiKohdetilit.slice(0, MAKSIMI_NAYTETTAVAT_OSUMAT)
    }
    const lower = haku.toLowerCase()
    const taysiOsuma: Kirjanpitotili[] = []
    const numeroOsuma: Kirjanpitotili[] = []
    const nimiOsuma: Kirjanpitotili[] = []
    for (const tili of this._kaikkiKohdetilit) {
      if (tili.numero === haku) {
        taysiOsuma.push(tili)
      } else if (tili.numero.startsWith(haku)) {
        numeroOsuma.push(tili)
      } else if (tili.nimi.toLowerCase().includes(lower)) {
        nimiOsuma.push(tili)
      }
    }
    return [...taysiOsuma, ...numeroOsuma, ...nimiOsuma].slice(0, MAKSIMI_NAYTETTAVAT_OSUMAT)
  }

  annaNayttoteksti(kohdetilinumero: string): string {
    if (!kohdetilinumero) {
      return ''
    }
    const tili = this._kohdetiliNimellaNumerolla.get(kohdetilinumero)
    return tili ? `${tili.numero} - ${tili.nimi}` : kohdetilinumero
  }

  paivitaHaku(teksti: string): void {
    this.hakuteksti = teksti
    this.suodatetutTilit = this._suodataTilit(teksti)
  }

  /**
   * Aktivoi rivin muokkaustilaan. Hakukenttä on aina aluksi tyhjä — käytössä vain etsimistä varten,
   * ei rivin nykyisen valinnan näyttämiseen. Listalle näytetään oletusosumat, ja paneli avataan ohjelmallisesti.
   */
  aktivoiRivi(rivi: TiliMappausRivi): void {
    if (this.aktiivinenRivinNumero === rivi.lahdetili.numero) {
      return
    }
    this.aktiivinenRivinNumero = rivi.lahdetili.numero
    this.hakuteksti = ''
    this.suodatetutTilit = this._suodataTilit('')
    // Annetaan Angularin renderöidä input ennen kuin sitä yritetään fokusoida + avata paneli.
    setTimeout(() => {
      this.aktiivinenTiliInput?.nativeElement.click()
    }, 50)
  }

  valitseTili(rivi: TiliMappausRivi, tilinumero: string): void {
    rivi.kohdetilinumero = tilinumero
    this.paivitaKaikkiMapattu()
    this._suljeAktiivinenRivi()
  }

  /**
   * Mat-autocompleten (closed) -event laukeaa AINA paneelin sulkeuduttua, myös optionSelectedin jälkeen.
   * Käytetään tätä eikä (blur) inputissa, koska blur voi laueta ennen optionSelectediä ja tuhota inputin
   * ennen kuin valinta ehtii välittyä. Jos käyttäjä oli kirjoittanut täsmälleen jonkin tilin numeron
   * eikä valinnut sitä klikkaamalla, valitaan se automaattisesti.
   */
  kasittelePanelinSulku(rivi: TiliMappausRivi): void {
    if (this.aktiivinenRivinNumero !== rivi.lahdetili.numero) {
      return
    }
    const kirjoitettu = this.hakuteksti.trim()
    const taysiOsuma = kirjoitettu ? this._kohdetiliNimellaNumerolla.get(kirjoitettu) : undefined
    if (taysiOsuma) {
      this.valitseTili(rivi, taysiOsuma.numero)
      return
    }
    this._suljeAktiivinenRivi()
  }

  private _suljeAktiivinenRivi(): void {
    this.aktiivinenRivinNumero = null
    this.hakuteksti = ''
    this.suodatetutTilit = []
    this._changeDetectorRef.markForCheck()
  }

  constructor(
    private _errorHandler: ErrorHandler,
    private _dateService: DateService,
    private _asiakasService: AsiakasService,
    private _asiakasJaettuService: AsiakasJaettuService,
    private _firebase: FirebaseLemonator,
    private _ladataanService: LadataanService,
    private _snackbar: MatSnackBar,
    private _tilikarttaService: TilikarttaService,
    private _kirjautunutKayttajaService: KirjautunutKayttajaService,
    private _debugService: DebugService,
    private _tiedostojenLataamisService: TiedostojenLataamisService,
    private _arkistoService: ArkistoService,
    private _route: ActivatedRoute,
    private _router: Router,
    private _dialog: MatDialog,
    private _changeDetectorRef: ChangeDetectorRef,
  ) {
    // Johdetut observablet parse-vastauksesta
    this.lahdetilitObservable = this._parseVastausSubject.pipe(map(v => v?.lahdetilit || []))
    this.parsitutViennitObservable = this._parseVastausSubject.pipe(map(v => v?.viennit || []))
    this.luetutTiedostonimetObservable = this._parseVastausSubject.pipe(map(v => v?.tiedostot ?? []))
    this.vientienAikavaliObservable = this.parsitutViennitObservable.pipe(
      map(viennit => {
        if (!viennit?.length) {
          return null
        }
        let min = viennit[0].pvm
        let max = viennit[0].pvm
        for (const v of viennit) {
          if (v.pvm < min) {
            min = v.pvm
          }
          if (v.pvm > max) {
            max = v.pvm
          }
        }
        const alkuDate = this._dateService.numberToDate(min)
        const loppuDate = this._dateService.numberToDate(max)
        return {
          alku: this._dateService.muotoilePaivaDate(alkuDate, 'fi'),
          loppu: this._dateService.muotoilePaivaDate(loppuDate, 'fi'),
        }
      }),
    )
  }

  ngOnInit(): void {
    // Debug
    this.kirjanpitajaOnDevaajaObservable = this._kirjautunutKayttajaService.kirjanpitajaOnDevaajaObservable
    this.tuontiUriObservable = this._asiakasService.nykyinenAsiakasAvainObservable.pipe(
      map(avainTiedot => {
        if (avainTiedot?.avain) {
          return this._kirjanpitoUriService.annaAlkusaldoImportDocumentUri(avainTiedot.avain)
        }
        return ''
      }),
    )
    this.tuontiEncodedUriObservable = this.tuontiUriObservable.pipe(
      map(uri => {
        return this._debugService.createFirestoreLink(uri)
      }),
    )

    this.tilikaudetObservable = this._asiakasService.nykyinenAsiakasObservable.pipe(map(asiakas => asiakas?.tilikaudet || []))

    this._asiakasService.nykyinenAsiakasObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(asiakas => {
      if (asiakas) {
        const nykyinenPaiva = this._dateService.currentLocalDate()
        const nykyinenTilikausi = this._asiakasJaettuService.annaTilikausiPaivalle(asiakas, nykyinenPaiva)
        if (nykyinenTilikausi) {
          this._tilikarttaService.asetaAktiivinenTilikausiKonteksti(nykyinenTilikausi, this._dateService.currentLocalMonth())
        } else if (asiakas.tilikaudet?.length) {
          const mikaTahansa = asiakas.tilikaudet[asiakas.tilikaudet.length - 1]
          this._tilikarttaService.asetaAktiivinenTilikausiKonteksti(mikaTahansa, mikaTahansa.loppuu)
        }
      }
      this.asiakas = asiakas
    })

    this.edellinenImportObservable = this._asiakasService.nykyinenAsiakasObservable.pipe(
      switchMap(asiakas => {
        if (!asiakas) {
          return of(null)
        }
        const uri = this._kirjanpitoUriService.annaAlkusaldoImportDocumentUri(asiakas.avain)
        return this._firebase.firestoreDoc<AlkusaldoImportDocument>(uri).listen()
      }),
    )

    this.tilikarttaTilitObservable = this._tilikarttaService.nykyisenAsiakkaanTilikartanJaPaatilikartanTilitObservable.pipe(
      map(tilit => {
        return tilit?.filter(t => t.aktiivinen && t.numero.length > 3) || []
      }),
    )

    // Historia-dokumentit — ladataan subcollectionista
    this.historiaObservable = this._asiakasService.nykyinenAsiakasObservable.pipe(
      switchMap(asiakas => {
        if (!asiakas) {
          return of([])
        }
        const uri = this._kirjanpitoUriService.annaAlkusaldoImportDocumentUri(asiakas.avain) + '/alkusaldo-import-historia'
        return this._firebase.firestoreCollection<AlkusaldoImportHistoriaDocument>(uri).listen()
      }),
      map(docs =>
        (docs || []).sort((a, b) => {
          const aTime = a.tuotu?.seconds || 0
          const bTime = b.tuotu?.seconds || 0
          return bTime - aTime
        }),
      ),
    )

    this.suodatettuHistoriaObservable = combineLatest([this.historiaObservable, this.historiasuodatinSubject]).pipe(
      map(([historia, suodatin]) => {
        if (suodatin === 'kaikki') {
          return historia
        }
        return historia.filter(h => h.tyyppi === suodatin)
      }),
    )

    // Mappausrivit rakennetaan jokaisesta parse-vastauksesta uudelleen, jotta "Muokkaa tiedostoja" → uusi luku päivittää rivit.
    // Tilikartta ja edellinen import luetaan vastauksen hetkellä kertaalleen (take(1)), etteivät live-päivitykset nollaa käyttäjän kohdistuksia.
    this._parseVastausSubject
      .pipe(
        filter(v => !!v),
        switchMap(parseVastaus => combineLatest([of(parseVastaus), this.tilikarttaTilitObservable, this.edellinenImportObservable]).pipe(take(1))),
        takeUntil(this._ngUnsubscribe),
      )
      .subscribe(([parseVastaus, tilikartanTilit, edellinenImport]) => {
        this._edellisetMappaukset = edellinenImport?.mappaukset ?? []
        if (!parseVastaus.lahdetilit || !tilikartanTilit) {
          this.mappausRivitSubject.next([])
          return
        }

        this._kaikkiKohdetilit = tilikartanTilit
        this._kohdetiliNimellaNumerolla = new Map(tilikartanTilit.map(t => [t.numero, t]))

        const rivit: TiliMappausRivi[] = parseVastaus.lahdetilit.map(lahdetili => {
          const edellinenMappaus = edellinenImport?.mappaukset?.find(m => m.lahdetilinumero === lahdetili.numero)
          const tilikartanTili = tilikartanTilit.find(t => t.numero === lahdetili.numero)
          const kohdetilinumero = edellinenMappaus?.kohdetilinumero || tilikartanTili?.numero || ''

          return {
            lahdetili,
            kohdetilinumero,
          }
        })

        this.mappausRivitSubject.next(rivit)
        this.paivitaKaikkiMapattu()
        this.vaihe = 'mapping'
        this._ladataanService.lopetaLataaminen()
        // OnPush: combineLatest-callback ajetaan Firebase-kutsun jälkeen async-kontekstissa, joten komponentti pitää merkitä tarkistettavaksi käsin.
        this._changeDetectorRef.markForCheck()
      })

    this._alustaValinta()
  }

  ngOnDestroy(): void {
    this._tuhottu = true
    for (const lataus of this._kaynnissaOlevatLataukset) {
      lataus.cancel()
    }
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  /** Esitäyttää valinnan edellisestä importista ja hallintokansiosta query-parametrilla tuodusta tiedostosta. */
  private async _alustaValinta(): Promise<void> {
    try {
      const asiakas = await firstValueFrom(this._asiakasService.nykyinenAsiakasObservable.pipe(filter(a => !!a)))
      this.asiakas = asiakas

      const edellinen = await this._firebase.firestoreDoc<AlkusaldoImportDocument>(this._kirjanpitoUriService.annaAlkusaldoImportDocumentUri(asiakas.avain)).get()
      asetaArvo(this.luoAlkusaldokirjaukset, edellinen?.luoAlkusaldokirjaukset ?? true)
      this._edellisetMappaukset = edellinen?.mappaukset ?? []

      const tiedostot = await Promise.all((edellinen?.tiedostot ?? []).map(t => this._lataaHallintokansionTiedosto(asiakas.avain, t.arkistoTiedostonAvain, t.tiedostonNimi)))

      const params = this._route.snapshot.queryParams
      const parametrinAvain = params['arkistoTiedostonAvain'] as string | undefined
      if (parametrinAvain) {
        if (!tiedostot.some(t => t.arkistoTiedostonAvain === parametrinAvain)) {
          tiedostot.push(await this._lataaHallintokansionTiedosto(asiakas.avain, parametrinAvain, params['tiedostonNimi'] as string))
        }
        await this._router.navigate([], { relativeTo: this._route, queryParams: {}, replaceUrl: true })
      }

      this.valitutTiedostotSubject.next(tiedostot)
      this.vaihe = 'valinta'
    } catch (err) {
      this._errorHandler.handleError(err)
      this.virhe = 'Tuontitietojen lataus epäonnistui'
      this.vaihe = 'error'
    } finally {
      this._changeDetectorRef.markForCheck()
    }
  }

  private async _lataaHallintokansionTiedosto(asiakasAvain: string, arkistoTiedostonAvain: string, tiedostonNimi: string | undefined): Promise<ValittuTuontitiedosto> {
    const metadata = await this._firebase.firestoreDoc<ArkistoFile>(this._arkistoService.getArkistoMetadataUri(asiakasAvain, arkistoTiedostonAvain)).get()
    return {
      arkistoTiedostonAvain,
      tiedostonNimi: tiedostonNimi || metadata?.fileName || '',
      koko: null,
      tila: 'hallintokansiossa',
      latausprosentti: 100,
      virheviesti: null,
      tilikausiAvaimet: metadata?.tilikaudet ?? [],
    }
  }

  private _hallintokansiossaOlevat(): ValittuTuontitiedosto[] {
    return this.valitutTiedostotSubject.value.filter(t => t.tila === 'hallintokansiossa' || t.tila === 'valmis')
  }

  private _paivitaTiedosto(arkistoTiedostonAvain: string, muutos: Partial<ValittuTuontitiedosto>): void {
    this.valitutTiedostotSubject.next(this.valitutTiedostotSubject.value.map(t => (t.arkistoTiedostonAvain === arkistoTiedostonAvain ? { ...t, ...muutos } : t)))
  }

  async lisaaTiedosto(entries: FileDropEntry[]): Promise<void> {
    this.vedetaanPaalle = false
    this.hylatytTiedostot = []

    const tiedostoEntryt = entries.filter(entry => entry.fileEntry?.isFile)
    if (!tiedostoEntryt.length) {
      return
    }
    if (tiedostoEntryt.length > 1) {
      this.hylatytTiedostot = ['Lisää yksi tiedosto kerrallaan, jotta jokaiselle tiedostolle voidaan valita omat tilikaudet.']
      this._changeDetectorRef.markForCheck()
      return
    }

    const tilikausiAvaimet = this.tilikausiAvaimet().value()
    if (!tilikausiAvaimet.length) {
      this.tilikausiAvaimet().markAsTouched()
      this.hylatytTiedostot = ['Valitse tilikaudet ennen tiedoston lisäämistä.']
      this._changeDetectorRef.markForCheck()
      return
    }

    const file = await this._tiedostojenLataamisService.getFile(tiedostoEntryt[0].fileEntry as FileSystemFileEntry)
    if (!file.name.toLowerCase().endsWith('.csv')) {
      this.hylatytTiedostot = [`${file.name}: vain .csv-tiedostot kelpaavat`]
    } else if (file.size === 0) {
      this.hylatytTiedostot = [`${file.name}: tiedosto on tyhjä`]
    } else if (this.valitutTiedostotSubject.value.some(t => t.tiedostonNimi === file.name)) {
      this.hylatytTiedostot = [`${file.name}: tiedosto on jo listalla`]
    } else {
      this.valitutTiedostotSubject.next([
        ...this.valitutTiedostotSubject.value,
        {
          arkistoTiedostonAvain: this._firebase.firestoreCreateId(),
          tiedostonNimi: file.name,
          koko: file.size,
          tila: 'odottaa',
          latausprosentti: 0,
          virheviesti: null,
          tilikausiAvaimet,
          file,
        },
      ])
      nollaa(this.tilikausiAvaimet, [])
    }
    this._changeDetectorRef.markForCheck()
  }

  poistaTiedosto(tiedosto: ValittuTuontitiedosto): void {
    if (this.lataaminenKesken) {
      return
    }
    this.valitutTiedostotSubject.next(this.valitutTiedostotSubject.value.filter(t => t.arkistoTiedostonAvain !== tiedosto.arkistoTiedostonAvain))
  }

  tiedostoVedetaan(paalla: boolean): void {
    this.vedetaanPaalle = paalla
    this._changeDetectorRef.markForCheck()
  }

  palaaValintaan(): void {
    this.virhe = null
    this.paallekkaisetTiedostot = []
    this.vaihe = 'valinta'
  }

  palaaKohdistukseen(): void {
    this.virhe = null
    this.paallekkaisetTiedostot = []
    this.vaihe = 'mapping'
  }

  muotoileKoko(tavut: number | null): string {
    if (tavut === null) {
      return ''
    }
    if (tavut < 1024 * 1024) {
      return `${Math.max(1, Math.round(tavut / 1024))} kt`
    }
    return `${(tavut / 1024 / 1024).toFixed(1)} Mt`
  }

  annaMetadataDebugLinkki(tiedosto: ValittuTuontitiedosto): string {
    return this._debugService.createFirestoreLink(this._arkistoService.getArkistoMetadataUri(this.asiakas.avain, tiedosto.arkistoTiedostonAvain))
  }

  annaBinaariDebugLinkki(tiedosto: ValittuTuontitiedosto): string {
    return this._debugService.annaGCBinaryLinkLemonator(
      this._debugService.annaArkistoPublicBucketNimi(),
      this._arkistoService.getArkistoPublicFileStorageUri(this.asiakas.avain, { avain: tiedosto.arkistoTiedostonAvain }),
    )
  }

  annaTiedostonimet(tiedostot: AlkusaldoTuontitiedosto[] | undefined): string {
    return tiedostot?.map(t => t.tiedostonNimi).join(', ') || '–'
  }

  /** Lataa uudet tiedostot hallintokansioon, tallentaa import-dokumentin ja käynnistää parsinnan. */
  async lueTiedostot(): Promise<void> {
    if (!this.asiakas || this.lataaminenKesken || !this.valitutTiedostotSubject.value.length) {
      return
    }

    this.virhe = null
    this.paallekkaisetTiedostot = []
    this.hylatytTiedostot = []
    this.lataaminenKesken = true

    try {
      const kaikkiOnnistui = await this._lataaUudetHallintokansioon()
      if (this._tuhottu || !kaikkiOnnistui) {
        return
      }
      this.vaihe = 'processing'
      this._ladataanService.aloitaLataaminen()
      await this._parsiTiedostot()
    } catch (err) {
      this._errorHandler.handleError(err)
      this.virhe = 'Tiedostojen tallennus hallintokansioon epäonnistui'
      this.vaihe = 'error'
    } finally {
      this.lataaminenKesken = false
      this._changeDetectorRef.markForCheck()
    }
  }

  private async _lataaUudetHallintokansioon(): Promise<boolean> {
    const asiakasAvain = this.asiakas.avain
    const uudet = this.valitutTiedostotSubject.value.filter(t => t.tila === 'odottaa' || t.tila === 'virhe')
    for (const tiedosto of uudet) {
      this._paivitaTiedosto(tiedosto.arkistoTiedostonAvain, { tila: 'ladataan', latausprosentti: 0, virheviesti: null })
    }

    const bucket = environment.environment === EnvironmentType.PRODUCTION ? 'arkisto-public' : 'arkisto-public-test'
    const lataukset = uudet.map(tiedosto => {
      const storageUri = this._arkistoService.getArkistoPublicFileStorageUri(asiakasAvain, { avain: tiedosto.arkistoTiedostonAvain })
      const uploadData = this._tiedostojenLataamisService.upload(this._firebase, storageUri, tiedosto.file, bucket)
      uploadData.donePercentage.pipe(takeUntil(this._ngUnsubscribe)).subscribe(prosentti => {
        this._paivitaTiedosto(tiedosto.arkistoTiedostonAvain, { latausprosentti: prosentti })
        this._changeDetectorRef.markForCheck()
      })
      return { tiedosto, uploadData }
    })
    this._kaynnissaOlevatLataukset = lataukset.map(l => l.uploadData.uploadTask)
    this.kokonaisprosenttiObservable = this._tiedostojenLataamisService.annaKokonaisprosentti(lataukset.map(l => l.uploadData))

    const tulokset = await Promise.allSettled(lataukset.map(l => firstValueFrom(l.uploadData.doneObservable)))
    this._kaynnissaOlevatLataukset = []
    if (this._tuhottu) {
      return false
    }

    const onnistuneet: ValittuTuontitiedosto[] = []
    tulokset.forEach((tulos, i) => {
      const { tiedosto, uploadData } = lataukset[i]
      if (tulos.status === 'fulfilled') {
        onnistuneet.push(tiedosto)
      } else {
        this._paivitaTiedosto(tiedosto.arkistoTiedostonAvain, { tila: 'virhe', virheviesti: uploadData.error?.message ?? 'Lataus epäonnistui' })
      }
    })

    const kirjanpitaja = await firstValueFrom(this._kirjautunutKayttajaService.kirjanpitajanTiedotObservable.pipe(take(1)))
    const nyt = this._firebase.firestoreTimestampFromDate(new Date())
    const batch = this._firebase.firestoreBatch()
    for (const tiedosto of onnistuneet) {
      const metadata: ArkistoFile = {
        avain: tiedosto.arkistoTiedostonAvain,
        fileName: tiedosto.tiedostonNimi,
        asiakasAvain,
        lisatiedot: '',
        poistettu: false,
        paivitetty: nyt,
        luotu: nyt,
        paivittaja: kirjanpitaja.uid,
        luoja: kirjanpitaja.uid,
        type: ArkistoFileType.VISIBLE_FOR_CUSTOMER,
        tilikaudet: tiedosto.tilikausiAvaimet,
        tiedostoTyyppi: ArkistoFileTiedostoTyyppi.ALKUSALDOT,
      }
      batch.set<ArkistoFile>(this._arkistoService.getArkistoMetadataUri(asiakasAvain, tiedosto.arkistoTiedostonAvain), metadata)
      this._paivitaTiedosto(tiedosto.arkistoTiedostonAvain, { tila: 'valmis', file: undefined })
    }
    batch.set<AlkusaldoImportDocument>(this._kirjanpitoUriService.annaAlkusaldoImportDocumentUri(asiakasAvain), await this._annaImportDoc(), { merge: true })
    await batch.commit()

    return tulokset.every(tulos => tulos.status === 'fulfilled')
  }

  private async _parsiTiedostot(): Promise<void> {
    try {
      this._tyhjennaLukutulos()
      const pyynto: AlkusaldoCsvParsePyynto = {
        asiakasAvain: this.asiakas.avain,
        arkistoTiedostonAvaimet: this._hallintokansiossaOlevat().map(t => t.arkistoTiedostonAvain),
      }
      const valittuLahdejarjestelma = this._valittuLahdejarjestelma()
      if (valittuLahdejarjestelma) {
        pyynto.lahdejarjestelma = valittuLahdejarjestelma
      }

      const vastaus = await this._firebase.functionsCall<AlkusaldoCsvParsePyynto, AlkusaldoCsvParseVastaus>('alkusaldoParseCsv', pyynto)

      if (!vastaus.onnistui || !vastaus.viennit || !vastaus.lahdetilit) {
        this.virhe = this._kaannaVirhekoodi(vastaus.virhekoodi)
        this.paallekkaisetTiedostot = vastaus.virhekonteksti?.paallekkaisetTiedostot ?? []
        this.vaihe = 'error'
        return
      }

      if (vastaus.lahdejarjestelma) {
        asetaArvo(this.lahdejarjestelma, vastaus.lahdejarjestelma)
      }
      this._parseVastausSubject.next(vastaus)
    } catch (err) {
      this._errorHandler.handleError(err)
      this.virhe = 'Virhe tiedostojen käsittelyssä'
      this.vaihe = 'error'
    } finally {
      this._ladataanService.lopetaLataaminen()
      this._changeDetectorRef.markForCheck()
    }
  }

  // Rivit ja luetut tiedostot kuuluvat aina viimeisimpään onnistuneeseen lukuun. Muuten epäonnistuneen tunnistuksen jälkeen voisi palata
  // vanhoihin riveihin ja käynnistää tuonnin, jolloin backend lukisi uuden tiedostojoukon Firestoreen jääneellä vanhalla lähdejärjestelmällä.
  private _tyhjennaLukutulos(): void {
    this._parseVastausSubject.next(null)
    this.mappausRivitSubject.next([])
    this.kaikkiMapattu = false
  }

  async tallennaMappausJaSiirryTilikausiin(): Promise<void> {
    await this.tallennaMappaus(false)
    if (this.asiakas) {
      this._router.navigate(['/asiakkaat', this.asiakas.avain, 'kirjanpitotiedot'])
    }
  }

  private async _annaImportDoc(): Promise<AlkusaldoImportDocument> {
    const rivit = this.mappausRivitSubject.value
    const mappaukset: AlkusaldoTiliMappaus[] = rivit.length
      ? rivit.map(rivi => ({
          lahdetilinumero: rivi.lahdetili.numero,
          kohdetilinumero: rivi.kohdetilinumero,
        }))
      : this._edellisetMappaukset

    const kirjautunutKayttaja = await firstValueFrom(this._kirjautunutKayttajaService.kirjanpitajanTiedotObservable.pipe(take(1)))
    const importDoc: AlkusaldoImportDocument = {
      tuotu: this._firebase.firestoreTimestampFromDate(new Date()),
      mappaukset,
      tuoja: kirjautunutKayttaja.uid,
      tiedostot: this._hallintokansiossaOlevat().map(t => ({ arkistoTiedostonAvain: t.arkistoTiedostonAvain, tiedostonNimi: t.tiedostonNimi })),
      luoAlkusaldokirjaukset: this.luoAlkusaldokirjaukset().value(),
    }
    // Firestore hylkää undefined-arvon, ja merge-tallennus säilyttää aiemmin tunnistetun järjestelmän
    const lahdejarjestelma = this._valittuLahdejarjestelma()
    if (lahdejarjestelma) {
      importDoc.lahdejarjestelma = lahdejarjestelma
    }
    return importDoc
  }

  private _valittuLahdejarjestelma(): AlkusaldoLahdejarjestelma | null {
    const valinta = this.lahdejarjestelma().value()
    return valinta === 'auto' ? null : valinta
  }

  /** Tallentaa import-dokumentin (mappaukset + tiedostotiedot) Firestoreen ilman historiaa. Merge säilyttää backendin ylläpitämän vaihdekirjauksetEstettyTilikaudet-listan. */
  private async _tallennaImportDoc(): Promise<AlkusaldoImportDocument> {
    const importDoc = await this._annaImportDoc()
    const uri = this._kirjanpitoUriService.annaAlkusaldoImportDocumentUri(this.asiakas.avain)
    await this._firebase.firestoreSetData(uri, importDoc, { merge: true })
    return importDoc
  }

  async tallennaMappaus(notifyUser: boolean = true): Promise<void> {
    if (!this.asiakas || !this._parseVastausSubject.value) {
      return
    }

    try {
      const importDoc = await this._tallennaImportDoc()

      const historiaDoc: AlkusaldoImportHistoriaDocument = {
        ...importDoc,
        tyyppi: AlkusaldoHistoriaTyyppi.VALITALLENNUS,
      }

      const historiaAvain = this._firebase.firestoreCreateId()
      const historiaUri = this._kirjanpitoUriService.annaAlkusaldoImportHistoriaUri(this.asiakas.avain, historiaAvain)
      await this._firebase.firestoreSetData(historiaUri, historiaDoc)

      if (notifyUser) {
        this._snackbar.open('Tiedot tallennettiin', 'OK', { duration: 3000 })
      }
    } catch (err) {
      this._errorHandler.handleError(err)
      if (notifyUser) {
        this._snackbar.open('Tietojen tallennus epäonnistui', 'OK', { duration: 3000 })
      }
    }
  }

  async aloitaTuonti(): Promise<void> {
    if (!this.kaikkiMapattu || !this.asiakas) {
      return
    }

    this.virhe = null
    this.paallekkaisetTiedostot = []
    this.vaihe = 'processing'
    this._ladataanService.aloitaLataaminen()

    try {
      // Tallennetaan import-dokumentti (mappaukset + tiedostotiedot) ennen tuontia
      await this._tallennaImportDoc()

      const pyynto: AlkusaldoKirjaustenLuontiPyynto = {
        asiakasAvain: this.asiakas.avain,
        alkuPvm: this._dateService.dateToNumber(this.alkuPvm().value()),
        loppuPvm: this._dateService.dateToNumber(this.loppuPvm().value()),
        luoAlkusaldokirjaukset: this.luoAlkusaldokirjaukset().value(),
      }

      const vastaus = await this._firebase.functionsCall<AlkusaldoKirjaustenLuontiPyynto, AlkusaldoKirjaustenLuontiVastaus>('alkusaldoLuoKirjaukset', pyynto)

      if (!vastaus.onnistui) {
        this.virhe = this._kaannaVirhekoodi(vastaus.virhekoodi)
        this.paallekkaisetTiedostot = vastaus.virhekonteksti?.paallekkaisetTiedostot ?? []
        this.vaihe = 'error'
        return
      }

      this.onnistumisviesti = `Tuonti onnistui: ${vastaus.luodutKirjaukset} kirjausta, ${vastaus.luodutViennit} vientiä.`
      if (vastaus.vaihdekirjauksetEstettyTilikaudet?.length) {
        this.onnistumisviesti += ` Tilikauden vaihteen automaattikirjaukset estetty tilikausilta: ${this.muotoileTilikaudet(vastaus.vaihdekirjauksetEstettyTilikaudet)}.`
      }
      if (vastaus.ohitetutTilikaudenTuloksenViennit) {
        this.onnistumisviesti += ` Tilikauden tulos -tilille (2370) osoitettuja vientejä ohitettiin ${vastaus.ohitetutTilikaudenTuloksenViennit} kpl, koska tase laskee tilikauden tuloksen automaattisesti.`
      }
      this.vaihe = 'done'
      this._snackbar.open(this.onnistumisviesti, 'OK', { duration: 5000 })
    } catch (err) {
      this._errorHandler.handleError(err)
      this.virhe = 'Kirjausten luonti epäonnistui'
      this.vaihe = 'error'
    } finally {
      this._ladataanService.lopetaLataaminen()
      this._changeDetectorRef.markForCheck()
    }
  }

  async poistaKaikkiAlkusaldot(): Promise<void> {
    if (!this.asiakas) {
      return
    }

    const data: AreYouSureDialogData = {
      header: 'Poista kaikki alkusaldokirjaukset',
      rightAction: 'Poista kaikki',
      text: 'Oletko varma, että haluat poistaa kaikki alkusaldokirjaukset? Toimintoa ei voi peruuttaa.',
    }
    const isSure = await firstValueFrom(this._dialog.open(AreYouSureDialog, { data }).afterClosed())
    if (!isSure) {
      return
    }

    this.virhe = null
    this.vaihe = 'processing'
    this._ladataanService.aloitaLataaminen()

    try {
      const pyynto: AlkusaldoPoistaPyynto = {
        asiakasAvain: this.asiakas.avain,
      }

      const vastaus = await this._firebase.functionsCall<AlkusaldoPoistaPyynto, AlkusaldoPoistaVastaus>('alkusaldoPoista', pyynto)

      if (!vastaus.onnistui) {
        this.virhe = this._kaannaVirhekoodi(vastaus.virhekoodi)
        this.vaihe = 'error'
        return
      }

      this.onnistumisviesti = `Poistettiin ${vastaus.poistetutKirjaukset} alkusaldokirjausta.`
      this.vaihe = 'done'
      this._snackbar.open(this.onnistumisviesti, 'OK', { duration: 5000 })
    } catch (err) {
      this._errorHandler.handleError(err)
      this.virhe = 'Poisto epäonnistui'
      this.vaihe = 'error'
    } finally {
      this._ladataanService.lopetaLataaminen()
      this._changeDetectorRef.markForCheck()
    }
  }

  muotoileTilikaudet(avaimet: string[]): string {
    return (this.asiakas?.tilikaudet ?? [])
      .filter(tk => avaimet.includes(tk.avain))
      .sort((a, b) => this._dateService.localDateToNumber(a.alkaa) - this._dateService.localDateToNumber(b.alkaa))
      .map(tk => `${this._dateService.muotoilePaikallinenPaiva(tk.alkaa, 'fi')} - ${this._dateService.muotoilePaikallinenPaiva(tk.loppuu, 'fi')}`)
      .join(', ')
  }

  private _kaannaVirhekoodi(koodi: AlkusaldoVirhekoodi): string {
    switch (koodi) {
      case AlkusaldoVirhekoodi.METADATA_EI_LOYDY:
        return 'Tiedoston metadataa ei löytynyt. Tarkista, että tiedosto on edelleen arkistossa.'
      case AlkusaldoVirhekoodi.TIEDOSTOA_EI_LOYDY:
        return 'Tiedostoa ei löytynyt tallennuspaikasta. Se on voitu poistaa tai siirtää.'
      case AlkusaldoVirhekoodi.CSV_OTSIKKORIVI_PUUTTUU:
        return 'CSV-tiedostosta ei löytynyt otsikkoriviä. Tarkista, että tiedosto on oikeassa muodossa.'
      case AlkusaldoVirhekoodi.CSV_SARAKKEET_PUUTTUVAT:
        return 'CSV-tiedostosta puuttuu pakollisia sarakkeita (Tili, Nimi, Päivämäärä, Debet, Kredit).'
      case AlkusaldoVirhekoodi.CSV_EI_VIENTEJA:
        return 'CSV-tiedostosta ei löytynyt yhtään kirjanpidon vientiä.'
      case AlkusaldoVirhekoodi.TIEDOSTOT_PAALLEKKAISIA:
        return 'Valituissa tiedostoissa on päällekkäisiä ajanjaksoja. Poista päällekkäiset tiedostot ja lue tiedostot uudelleen.'
      case AlkusaldoVirhekoodi.PUUTTUVIA_MAPPAUKSIA:
        return 'CSV-tiedostossa on vientejä, joille ei löydy tilikartan mukaista kohdistusta.'
      case AlkusaldoVirhekoodi.TILIKAUTTA_EI_LOYDY:
        return 'Alkusaldokirjausten luonti epäonnistui: ensimmäisen vientiä edeltävää tilikautta ei löytynyt.'
      case AlkusaldoVirhekoodi.EI_MAPPAUKSIA: {
        const alku = this.alkuPvm().value() ? this._dateService.muotoilePaivaDate(this.alkuPvm().value(), 'fi') : '-'
        const loppu = this.loppuPvm().value() ? this._dateService.muotoilePaivaDate(this.loppuPvm().value(), 'fi') : '-'
        if (this.alkuPvm().value() || this.loppuPvm().value()) {
          return `Siirtotiedostosta ei löytynyt yhtään vientiä valitsemallesi aikavälille ${alku} - ${loppu}.`
        }
        return 'Siirtotiedostosta ei löytynyt yhtään vientiä jotka vastaisivat tilikarttakohdistusta.'
      }
      case AlkusaldoVirhekoodi.SALDO_PUUTTUU:
        return 'Alkusaldokirjausten luonti epäonnistui: tilin ensimmäiseltä vienniltä puuttuu saldo.'
      case AlkusaldoVirhekoodi.ALKUSALDOJA_EI_TUETA:
        return 'Valittu lähdejärjestelmä ei tue alkusaldojen laskentaa siirtotiedostosta.'
      case AlkusaldoVirhekoodi.LAHDEJARJESTELMA_EPAVARMA:
        return 'Lähdejärjestelmää ei voitu tunnistaa tiedostoista automaattisesti tai tiedostot ovat eri järjestelmistä. Valitse lähdejärjestelmä ja lue tiedostot uudelleen.'
      case AlkusaldoVirhekoodi.TUNTEMATON_VIRHE:
        return 'Toiminto epäonnistui tuntemattoman virheen vuoksi. Yritä uudelleen.'
      default:
        return 'Tuntematon virhe.'
    }
  }
}
